/**
 * Parses a text string containing link placeholders and bold tags, converting them into React elements.
 *
 * @param text - The input text string containing optional link placeholders in the format `<index>text</index>` and bold tags in the format `<b>text</b>`
 * @param links - An array of URLs corresponding to the link placeholders by index
 * @returns An array of React nodes containing text, anchor elements, and strong elements
 *
 * @example
 * ```typescript
 *
 * const result = parseText(
 *   'Click <0>here</0> for <b>important</b> info',
 *   ['https://example.com']
 * );
 * // Returns: [text node, <a>here</a>, text node, <strong>important</strong>, text node]
 *
 * const result2 = parseText(
 *   'No links here, just <b>bold</b> text',
 *   []
 * );
 * // Returns: [text node, <strong>bold</strong>, text node]
 *
 * @remarks
 * - Link placeholders use zero-based indexing to reference URLs in the links array
 * - URLs without a protocol are prefixed with 'https://'
 * - Links open in a new tab with security attributes (noopener, noreferrer)
 * - Invalid or missing link indices default to href="#"
 * - Empty input text returns an empty array
 * - Bold tags are converted to <strong> elements with a "font-bold" class
 */
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export const parseText = (text: string, links?: string[]): ReactNode[] => {
  if (!text) return [];

  const segments = text.split(/(<\d+>.*?<\/\d+>|<b>.*?<\/b>)/g);

  return segments.map((segment, i) => {
    const textToLink = segment.match(/^<(\d+)>(.*?)<\/\1>$/);
    if (textToLink && links) {
      const href = links[Number(textToLink[1])];

      // Determine the full href:
      // - If href is missing/empty, default to '#'
      // - If href starts with a supported scheme, use it as-is
      // - Otherwise, prepend https://
      let fullHref: string;
      if (!href) {
        fullHref = '#';
      } else if (/^(https?:|mailto:|tel:|fax:)/.test(href)) {
        fullHref = href;
      } else {
        fullHref = `https://${href}`;
      }
      return (
        <Link
          className="text-body-link-strong"
          key={i}
          to={fullHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {textToLink[2]}
        </Link>
      );
    }
    const boldMatch = segment.match(/^<b>(.*?)<\/b>$/);
    if (boldMatch) {
      return (
        <strong key={i} className="font-bold">
          {boldMatch[1]}
        </strong>
      );
    }

    // Check is segment is ul or ol tag
    const listMatch = segment.match(/^<(ul|ol)>(.*?)<\/\1>$/s);
    if (listMatch) {
      const ListTag = listMatch[1] as 'ul' | 'ol';
      const listItems = listMatch[2].split(/<li>(.*?)<\/li>/g).filter((item) => item.trim() !== '');
      return (
        <ListTag key={i} className="list-disc pl-5 mb-space-300">
          {listItems.map((item, index) => (
            <li key={index} className="p-1">
              {item}
            </li>
          ))}
        </ListTag>
      );
    }

    // return plain text if no match
    return segment;
  });
};
